import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
`;

const Dialog = styled.div`
  margin: 0;
  font-family: Verdana,Arial,sans-serif;
  font-size: 1.1em;
  border: 1px solid #aaaaaa;
  background: #ffffff 50% 50% repeat-x;
  color: #222222;
  border-radius: 4px;
  position: relative;
  padding: .2em;
  width: 450px;
  outline: none;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eeeeee;
  padding: .4em .6em;
  border-bottom: 1px solid #cccccc;
  .title {
    font-weight: bold;
  }
  .close {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.2em;
    line-height: 1;
  }
`;

const Content = styled.div`
  padding: 1em .5em;
  font-size: 0.95em;
  line-height: 1.4;
  & > div { margin-left: 15px; }
  .counts b { font-size: 1.2em; }
  .disclaimer {
    border: 1px solid #000;
    padding: 8px;
    font-size: 0.8em;
    text-align: justify;
    margin: .8em 1em;
    background: #fafafa;
  }
`;

const ButtonPane = styled.div`
  padding: .5em;
  text-align: right;
  button {
    margin-left: .5em;
    padding: .4em .8em;
    font-size: 0.9em;
    border: 1px solid #888;
    background: #f0f0f0;
    border-radius: 3px;
    cursor: pointer;
    &:hover { background: #e0e0e0; }
  }
`;

export default function MassCertifyModal({
  show,
  invoiceId,
  teachers = [],
  onCancel,
  onConfirm
}) {
  // how many will be touched?
  const [counts, setCounts] = useState({
    schools: 0,
    teachers: 0,
    implementations: 0,
  });

  // track whether we're showing the counts‐dialog (true) or the result modal (false)
  const [showResult, setShowResult] = useState(false);
  const [resultMsg, setResultMsg]   = useState("");
  const [resultStatus, setResultStatus] = useState("Success");

  // when `show` goes true, fetch counts
  useEffect(() => {
    if (!show) return;
    setShowResult(false);
    API.confirmAllCertification({ invoice_id: invoiceId })
      .then(resp => {
        const grades = resp.grades || [];
        const implIds    = new Set(grades?.map(g => g.implementation_id));
        const teacherIds = new Set();
        const schoolIds  = new Set();

        for (let implId of implIds) {
          const t = teachers?.find(t => t.implementation_id === implId);
          if (t) {
            teacherIds.add(t.customer_user_id);
            if (t.school_id) schoolIds.add(t.school_id);
          }
        }

        setCounts({
          implementations: implIds.size,
          teachers:       teacherIds.size,
          schools:        schoolIds.size,
        });
      })
      .catch(err => {
        console.error("Failed to fetch certification info:", err);
        setCounts({ implementations: 0, teachers: 0, schools: 0 });
      });
  }, [show, invoiceId, teachers]);

  if (!show) return null;

  // user clicked OK on the counts dialog
  const handleOk = () => {
    API.certifyAllImplementation({ invoice_id: invoiceId })
      .then(resp => {
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        setResultMsg(resp.message || (status === "Failed" ? "Certification failed." : "Certification complete."));
        setResultStatus(status);
        setShowResult(true);
      })
      .catch(err => {
        console.error("Failed to certify all:", err);
        setResultMsg(err.message || "Certification failed.");
        setResultStatus("Failed");
        setShowResult(true);
      });
  };

  // user dismissed the final ConfirmationModal
  const handleFinalClose = () => {
    setShowResult(false);
    setResultStatus("Success");
    onConfirm && onConfirm();
  };

  return (
    <>
      {!showResult && (
        <Overlay>
          <Dialog role="dialog" aria-labelledby="mass_certify_title">
            <TitleBar>
              <span className="title" id="mass_certify_title">
                Certify All Implementations
              </span>
              <button className="close" onClick={onCancel} aria-label="Close">×</button>
            </TitleBar>
            <Content>
              <div className="counts">
                <b>{counts.schools}</b> school(s),<br/>
                <b>{counts.teachers}</b> teacher(s),<br/>
                <b>{counts.implementations}</b> implementation(s) will be affected.
              </div>
              <div className="disclaimer">
                By clicking "OK" I certify that the lesson plans and/or curriculum resources downloaded were used to educate students in the classroom. I understand that this certification of educator implementation data will appear in documentation/reports which may be submitted to verify compliance with the amended Children's Internet Protection Act and FCC Rule - specifically: the school, school board, local educational agency, or other authority with responsibility for administration of the school to certify that, "as part of its Internet safety policy, [it] is educating minors about appropriate online behavior, including interacting with other individuals on social networking websites and in chat rooms and cyberbullying awareness and response", to be eligible for reimbursement through the E-Rate program.
              </div>
              <div style={{ marginLeft: 15 }}>Proceed?</div>
            </Content>
            <ButtonPane>
              <button onClick={onCancel}>Cancel</button>
              <button onClick={handleOk}>OK</button>
            </ButtonPane>
          </Dialog>
        </Overlay>
      )}

      <ConfirmationModal
        show={showResult}
        title={showResult ? "Certification Status" : ""}
        message={resultMsg}
        onCancel={handleFinalClose}
        onConfirm={handleFinalClose}
        status={resultStatus}
        single
      />
    </>
  );
}
